import { config } from '@/config';
import { CAMPAIGN_SESSION_STORAGE_KEY } from '@/constants';
import type { CollectionBase } from '@/features/events/types';
import { getLocalTimeZone } from '@/utils/dates';

const OUTLOOK_URL =
  'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
// Admin consent is a work/school-only concept, so it uses the `organizations`
// authority (personal accounts have no tenant admin).
const ADMIN_CONSENT_URL =
  'https://login.microsoftonline.com/organizations/v2.0/adminconsent';
const REDIRECT_PATH = '/integration/outlook';
const ADMIN_CONSENT_REDIRECT_PATH = '/integration/outlook/adminconsent';

// OIDC + offline_access (for a refresh token) + the account email.
const AUTH_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'User.Read',
];

export const READ_CALENDAR_SCOPES = ['Calendars.Read'];
export const WRITE_SCOPES = ['Calendars.ReadWrite'];
export const CALENDAR_SCOPES = [...WRITE_SCOPES];

export type Options = {
  /** Pre-fills the Microsoft account chooser. */
  email?: string;
};

export const getSigninUrl = (
  redirectTo = '/',
  state: Record<string, string | number | boolean>,
  { email }: Options = {}
): string => {
  const scopes = [...AUTH_SCOPES, ...CALENDAR_SCOPES];

  const payload: Record<string, string> = {
    client_id: config.outlookAuth.clientId,
    redirect_uri: `${config.server.url}${REDIRECT_PATH}`,
    response_type: 'code',
    response_mode: 'query',
    scope: scopes.join(' '),
    prompt: 'consent',
    state: JSON.stringify({ ...state, redirectTo }),
  };

  if (email) {
    payload.login_hint = email;
  }

  const query = new URLSearchParams(payload);

  return `${OUTLOOK_URL}?${query.toString()}`;
};

// Builds the return path/query so the server can redirect the user back where
// they started. When a magic CollectionBase is supplied we also carry the
// saved chat (?c=) or job (?e=) id so the magic page reloads those events on
// return. Mirrors getRedirectQuery in addToGoogle/util.lib.ts.
const buildRedirectUrl = (
  base?: CollectionBase | null,
  queryParams: Record<string, string> = {}
): string => {
  const redirectParams: Record<string, string> = { ...queryParams };
  if (base?.chat?.id && base.chat.isSaved) {
    redirectParams.c = base.chat.id;
  } else if (base?.job?.id) {
    redirectParams.e = base.job.id;
  }

  // set, not append: the magic page already carries its own ?e= (or ?c=), so
  // appending would leave two, and URLSearchParams.get returns the *first* —
  // restoring whichever id was in the URL rather than the one we just resolved.
  const query = new URLSearchParams(location.search);
  Object.entries(redirectParams).forEach(([key, value]) => {
    query.set(key, value);
  });

  const search = query.size > 0 ? `?${query.toString()}` : '';
  return `${window.location.pathname}${search}`;
};

// Assembles the full authorize URL, preserving the current path/query (and the
// magic job/chat id) so the server can redirect the user back where they
// started — otherwise the launch-resume handler has no event card to reopen the
// export modal against.
export const getAuthUrl = (
  queryParams: Record<string, string> = {},
  options: Options = {},
  base?: CollectionBase | null
): string => {
  const state: Record<string, string | boolean | number> = {
    app: 'magic',
    timeZone: getLocalTimeZone(),
  };

  const campaign = sessionStorage.getItem(CAMPAIGN_SESSION_STORAGE_KEY);
  if (campaign) {
    state.campaign = campaign;
  }

  return getSigninUrl(buildRedirectUrl(base, queryParams), state, options);
};

// Admin-consent URL for org/school tenants. A tenant admin grants Agenda Hero
// tenant-wide consent (required for Calendars.ReadWrite under the default Entra
// consent policy) so members can then connect without individual admin
// approval. Microsoft returns to ADMIN_CONSENT_REDIRECT_PATH with
// `admin_consent=True` (or an error), which the server maps back to the UI.
export const getAdminConsentUrl = (base?: CollectionBase | null): string => {
  const scopes = [...AUTH_SCOPES, ...CALENDAR_SCOPES];
  const payload: Record<string, string> = {
    client_id: config.outlookAuth.clientId,
    redirect_uri: `${config.server.url}${ADMIN_CONSENT_REDIRECT_PATH}`,
    scope: scopes.join(' '),
    state: JSON.stringify({
      app: 'magic',
      timeZone: getLocalTimeZone(),
      redirectTo: buildRedirectUrl(base),
    }),
  };

  return `${ADMIN_CONSENT_URL}?${new URLSearchParams(payload).toString()}`;
};
